import React, { useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import ZoomInOutlined from "@mui/icons-material/ZoomInOutlined";
import ZoomOutOutlined from "@mui/icons-material/ZoomOutOutlined";
import OpenInFullOutlined from "@mui/icons-material/OpenInFullOutlined";
import FolderOutlined from "@mui/icons-material/FolderOutlined";
import Inventory2Outlined from "@mui/icons-material/Inventory2Outlined";
import MemoryOutlined from "@mui/icons-material/MemoryOutlined";
import GroupsOutlined from "@mui/icons-material/GroupsOutlined";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import AccountTreeOutlined from "@mui/icons-material/AccountTreeOutlined";
import { ONTOLOGY_WORKBENCH_STEPS } from "../../data/create-ontology-questions.data";
import {
  GRAPH_NODES,
  GRAPH_EDGES,
  SELECTED_CONCEPT,
  GraphNodeType,
  ApprovalStageBadge
} from "../../data/create-ontology-build-knowledge-graph.data";
import "./CreateOntologyBuildKnowledgeGraph.scss";

const NODE_ICON: Record<GraphNodeType, React.ReactNode> = {
  asset: <FolderOutlined fontSize="inherit" />,
  fpso: <Inventory2Outlined fontSize="inherit" />,
  equipment: <MemoryOutlined fontSize="inherit" />,
  contractor: <GroupsOutlined fontSize="inherit" />,
  system: <AccountTreeOutlined fontSize="inherit" />,
  document: <DescriptionOutlined fontSize="inherit" />
};

const APPROVAL_TONE_CLASS: Record<ApprovalStageBadge["tone"], string> = {
  amber: "create-kg-approval-badge--amber",
  blue: "create-kg-approval-badge--blue",
  green: "create-kg-approval-badge--green"
};

const CreateOntologyBuildKnowledgeGraph: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const initiativeId = (location.state as { initiativeId?: string } | null)
    ?.initiativeId;

  const nodesById = useMemo(
    () => new Map(GRAPH_NODES.map((node) => [node.id, node])),
    []
  );

  const handleBack = () => {
    navigate("/ontology-studio/create/ground-to-documents", {
      state: { initiativeId }
    });
  };

  // Next step (Validate & Release) design not provided yet — return to the dashboard for now.
  const handleContinue = () => {
    navigate("/create-ontology");
  };

  const handleContinueToValidate = () => {
    navigate("/ontology-studio/create/validate-release", {
      state: { initiativeId }
    });
  };

  return (
    <div className="create-kg-container">
      <div className="create-kg-title-row">
        <div className="create-kg-title-block">
          <nav className="create-kg-breadcrumb">
            <Link to="/home" className="breadcrumb-link">
              Engineering Information Management
            </Link>
            <span className="breadcrumb-separator">&gt;</span>
            <Link to="/create-ontology" className="breadcrumb-link">
              Ontology Studio
            </Link>
          </nav>
          <h1 className="create-kg-title">Build Knowledge Graph</h1>
          <p className="create-kg-description">
            This dedicated lifecycle stage instantiates approved entities,
            generates the knowledge graph structure, creates relationships,
            binds evidence, and creates lineage and provenance for the selected
            concept.
          </p>
        </div>
        <div className="create-kg-meta">
          <span className="create-kg-meta__pill">
            Lifecycle Stage <strong>Build Knowledge Graph</strong>
          </span>
          <span className="create-kg-meta__pill">
            Initiative ID <strong>{initiativeId ?? "EY-ONT-2026-TEMP"}</strong>
          </span>
          <span className="create-kg-meta__pill">
            Status <strong>Draft</strong>
          </span>
        </div>
      </div>

      <div className="create-kg-workflow">
        <div className="create-kg-workflow__row">
          {ONTOLOGY_WORKBENCH_STEPS.slice(0, 3).map((step) => (
            <div
              key={step.id}
              className="create-kg-workflow__step create-kg-workflow__step--done"
            >
              <span className="create-kg-workflow__indicator">✓</span>
              <span className="create-kg-workflow__label">{step.label}</span>
            </div>
          ))}
        </div>
        <div className="create-kg-workflow__row">
          {ONTOLOGY_WORKBENCH_STEPS.slice(3, 6).map((step) => (
            <div
              key={step.id}
              className={`create-kg-workflow__step${
                step.id === 5 ? " create-kg-workflow__step--active" : ""
              }${step.id < 5 ? " create-kg-workflow__step--done" : ""}`}
            >
              <span className="create-kg-workflow__indicator">
                {step.id < 5 ? "✓" : step.id === 5 ? "•" : ""}
              </span>
              <span className="create-kg-workflow__label">{step.label}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="create-kg-content">
        <div className="create-kg-graph-card">
          <div className="create-kg-graph-card__header">
            <p className="create-kg-graph-card__title">
              Knowledge Graph Builder
            </p>
            <div className="create-kg-graph-card__tools">
              <ZoomInOutlined fontSize="small" />
              <ZoomOutOutlined fontSize="small" />
              <OpenInFullOutlined fontSize="small" />
            </div>
          </div>

          <div className="create-kg-canvas">
            <svg
              className="create-kg-canvas__edges"
              preserveAspectRatio="none"
              viewBox="0 0 100 100"
            >
              {GRAPH_EDGES.map((edge) => {
                const from = nodesById.get(edge.fromId);
                const to = nodesById.get(edge.toId);
                if (!from || !to) return null;
                return (
                  <line
                    key={`${edge.fromId}-${edge.toId}`}
                    x1={from.left}
                    y1={from.top}
                    x2={to.left}
                    y2={to.top}
                    className="create-kg-canvas__edge-line"
                  />
                );
              })}
            </svg>

            {GRAPH_EDGES.map((edge) => {
              const from = nodesById.get(edge.fromId);
              const to = nodesById.get(edge.toId);
              if (!from || !to) return null;
              return (
                <span
                  key={`label-${edge.fromId}-${edge.toId}`}
                  className="create-kg-canvas__edge-label"
                  style={{
                    top: `${(from.top + to.top) / 2}%`,
                    left: `${(from.left + to.left) / 2}%`
                  }}
                >
                  → {edge.label}
                </span>
              );
            })}

            {GRAPH_NODES.map((node) => (
              <div
                key={node.id}
                className={`create-kg-node${
                  node.highlighted ? " create-kg-node--highlighted" : ""
                }`}
                style={{ top: `${node.top}%`, left: `${node.left}%` }}
              >
                <span className="create-kg-node__icon">
                  {NODE_ICON[node.type]}
                </span>
                <span className="create-kg-node__label">{node.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="create-kg-concept-panel">
          <p className="create-kg-concept-panel__title">Selected Concept</p>

          <div className="create-kg-concept-section">
            <p className="create-kg-concept-section__label">Concept Name</p>
            <div className="create-kg-concept-name-row">
              <span className="create-kg-concept-name">
                {SELECTED_CONCEPT.name}
              </span>
              <span className="create-kg-status-pill">
                {SELECTED_CONCEPT.statusLabel}
              </span>
            </div>
          </div>

          <div className="create-kg-concept-section">
            <p className="create-kg-concept-section__label">Definition</p>
            <p className="create-kg-concept-section__text">
              {SELECTED_CONCEPT.definition}
            </p>
          </div>

          <div className="create-kg-concept-section">
            <p className="create-kg-concept-section__label">
              Attributes ({SELECTED_CONCEPT.attributes.length})
            </p>
            <div className="create-kg-chip-row">
              {SELECTED_CONCEPT.attributes.map((attribute) => (
                <span key={attribute} className="create-kg-chip">
                  {attribute}
                </span>
              ))}
            </div>
          </div>

          <div className="create-kg-concept-section">
            <p className="create-kg-concept-section__label">Relationships</p>
            <div className="create-kg-kv-list">
              {SELECTED_CONCEPT.relationships.map((relationship) => (
                <div
                  key={`${relationship.name}-${relationship.target}`}
                  className="create-kg-kv-row"
                >
                  <span className="create-kg-kv-row__key">
                    {relationship.name}
                  </span>
                  <span className="create-kg-kv-row__value">
                    {relationship.target}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="create-kg-concept-section">
            <p className="create-kg-concept-section__label">Source</p>
            <p className="create-kg-concept-section__text create-kg-concept-section__text--medium">
              {SELECTED_CONCEPT.source}
            </p>
          </div>

          <div className="create-kg-concept-section">
            <p className="create-kg-concept-section__label">
              Reuse Classification
            </p>
            <p className="create-kg-concept-section__link">
              {SELECTED_CONCEPT.reuseClassification}
            </p>
          </div>

          <div className="create-kg-concept-section">
            <p className="create-kg-concept-section__label">
              Lineage &amp; Provenance
            </p>
            <div className="create-kg-kv-list">
              {SELECTED_CONCEPT.lineageProvenance.map((entry) => (
                <div key={entry.label} className="create-kg-kv-row">
                  <span className="create-kg-kv-row__key">{entry.label}</span>
                  <span className="create-kg-kv-row__value">{entry.value}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="create-kg-concept-section">
            <p className="create-kg-concept-section__label">
              Approval Status Lifecycle
            </p>
            <div className="create-kg-chip-row">
              {SELECTED_CONCEPT.approvalStatusLifecycle.map((badge) => (
                <span
                  key={badge.label}
                  className={`create-kg-approval-badge ${
                    APPROVAL_TONE_CLASS[badge.tone]
                  }`}
                >
                  {badge.label}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="create-kg-footer">
        <button
          type="button"
          className="create-kg-btn create-kg-btn--secondary"
          onClick={handleBack}
        >
          Back
        </button>
        <div className="create-kg-footer__right">
          <button
            type="button"
            className="create-kg-btn create-kg-btn--secondary"
            onClick={handleContinue}
          >
            Save Draft
          </button>
          <button
            type="button"
            className="create-kg-btn create-kg-btn--primary"
            onClick={handleContinueToValidate}
          >
            Continue to Validate →
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateOntologyBuildKnowledgeGraph;
